import { Link } from "react-router-dom";
import MarketingNav from "../../components/marketing/MarketingNav";
import MarketingFooter from "../../components/marketing/MarketingFooter";
import {
  Anchor, ArrowUpRight, Package, Wrench, ClipboardList,
  Wallet, FileText, Image as ImageIcon, BookOpen, Ship,
  ShieldCheck, Users, BarChart3,
} from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/15564127/pexels-photo-15564127.jpeg";
const PROFILE_1 = "https://images.unsplash.com/photo-1648997578921-39af26168d93";
const PROFILE_2 = "https://images.unsplash.com/photo-1612764324168-7a3a318e0cbb";
const CREW = "https://images.unsplash.com/photo-1688223467306-64461bd749dd";
const TEAM = "https://images.unsplash.com/photo-1758518731468-98e90ffd7430";

export default function Landing() {
  return (
    <div className="bg-white text-ink">
      <MarketingNav />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-12 lg:pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs text-zinc-700 mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Trusted by captains across the Med & Gulf
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl tracking-tightest leading-[1.02] font-medium text-balance">
              Your yacht’s<br />
              <span className="text-zinc-400">complete digital</span><br />
              control center.
            </h1>
            <p className="mt-7 text-zinc-600 text-lg max-w-xl leading-relaxed">
              Inventory, maintenance, logbooks, documents, deckhand tasks and petty cash —
              one secure platform for owners, captains and crew. Built for boats that actually leave the dock.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/request-access"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 rounded-md bg-ink text-white px-5 py-3 text-sm font-medium hover:bg-black transition-colors"
              >
                Request access <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/features"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-5 py-3 text-sm font-medium hover:bg-zinc-50 transition-colors"
              >
                Tour the platform
              </Link>
            </div>
            <div className="mt-10 flex items-center gap-5 text-xs text-zinc-500">
              <div>Currently in private beta</div>
              <div className="h-3 w-px bg-zinc-300" />
              <div>By invitation only</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden border border-zinc-200">
                <img src={HERO_IMG} alt="Luxury yacht at sea" className="h-full w-full object-cover" />
              </div>
              {/* floating dashboard card */}
              <div className="absolute -left-6 -bottom-8 hidden md:block w-72 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-zinc-500">
                  Today onboard
                </div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="font-display text-3xl font-medium">12</div>
                  <div className="text-xs text-zinc-500">open tasks</div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <Stat label="Maintenance" value="4" />
                  <Stat label="Inventory" value="2" />
                  <Stat label="Documents" value="1" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* subtle bottom rule */}
        <div className="border-t border-zinc-200" />
      </section>

      {/* ── Trust strip ── */}
      <section className="py-10 border-b border-zinc-200 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            ["240+", "yachts under management"],
            ["18", "countries"],
            ["12 yr", "captain experience built-in"],
            ["99.9%", "uptime"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-3xl font-medium">{n}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bento features ── */}
      <section className="py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-14">
            <div className="md:col-span-5">
              <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">
                Operations Stack
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-medium tracking-tight leading-tight">
                Everything a yacht
                <br />needs to operate.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 text-zinc-600 leading-relaxed">
              <p>
                Seavanto replaces clipboards, WhatsApp groups, and Excel sheets with a
                single source of truth across every system, crew member and currency
                onboard.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <FeatureTile className="md:col-span-3 md:row-span-2 p-8" icon={Wrench} title="Maintenance"
              copy="Every job has a system, a status, a technician and a paper trail. Recurring jobs run themselves." />
            <FeatureTile className="md:col-span-3 p-8" icon={Package} title="Inventory"
              copy="Spare parts, safety gear, consumables — with low-stock alerts and part numbers attached." />
            <FeatureTile className="md:col-span-3 p-8" icon={ClipboardList} title="Deckhand Tasks"
              copy="Crisp daily checklists with photo proof. The aft deck never gets ‘forgotten’ again." />
            <FeatureTile className="md:col-span-2 p-8" icon={BookOpen} title="Digital Logbook"
              copy="Daily, trip, engine and incident logs that look great on PDF." />
            <FeatureTile className="md:col-span-2 p-8" icon={Wallet} title="Petty Cash"
              copy="Receipts in, approvals out. Multi-currency, audit-ready." />
            <FeatureTile className="md:col-span-2 p-8" icon={FileText} title="Documents"
              copy="Insurance, class, certificates — all with expiry alerts." />
            <FeatureTile className="md:col-span-3 p-8" icon={ImageIcon} title="Photo evidence"
              copy="Before / after photos attached to every maintenance job and task." />
            <FeatureTile className="md:col-span-3 p-8" icon={BarChart3} title="Reports & exports"
              copy="Monthly expense, service history and inventory exports for owners & auditors." />
          </div>
        </div>
      </section>

      {/* ── Multi-yacht / multi-role ── */}
      <section className="py-24 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">
              Built for fleets
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-medium tracking-tight leading-tight">
              One platform.<br /> One owner. Many yachts.
            </h2>
            <p className="mt-6 text-zinc-600 max-w-md leading-relaxed">
              Switch yachts in a single click. Each vessel has its own crew,
              inventory, jobs, reports and approvals — but you keep a single
              owner view across the fleet.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Link
                to="/request-access"
                className="text-sm px-4 py-2 rounded-md bg-ink text-white"
                data-testid="fleet-cta-primary"
              >
                Request access
              </Link>
              <Link to="/for-providers" className="text-sm px-4 py-2 rounded-md border border-zinc-200 bg-white">
                For service providers
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-12 gap-4">
              <YachtCard className="col-span-12 sm:col-span-7" name="M/Y Aurora" img={PROFILE_1} sub="32m · Sunseeker" />
              <YachtCard className="col-span-12 sm:col-span-5" name="M/Y Polaris" img={PROFILE_2} sub="24m · Princess" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Roles ── */}
      <section className="py-24" data-testid="roles-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-5">
              <div className="text-xs tracking-[0.2em] uppercase font-semibold text-zinc-500 mb-3">
                For every seat onboard
              </div>
              <h2 className="font-display text-3xl lg:text-5xl font-medium tracking-tight">
                A tailored view for owners, crew & accountants.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 text-zinc-600 leading-relaxed">
              Granular permissions mean the deckhand only sees today’s task list,
              the accountant only sees the ledger, and the owner sees everything.
              No more shared logins. No more leaking documents.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <RoleCard to="/for-owners"    title="Owners"    copy="Operational visibility & financial control." img={PROFILE_1} />
            <RoleCard to="/for-captains"  title="Captains"  copy="Run the daily operation in one app." img={CREW} />
            <RoleCard to="/for-crew"      title="Crew"      copy="Deckhand and engineer task lists." img={PROFILE_2} />
            <RoleCard to="/for-providers" title="Providers" copy="Get hired, get paid, get rebooked." img={TEAM} />
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-ink text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.2em] uppercase font-semibold text-white/50 mb-4">
              Ready when you are
            </div>
            <h2 className="font-display text-3xl lg:text-5xl font-medium tracking-tight leading-tight">
              Bring your yacht out of the
              <br /><span className="text-white/60">filing cabinet.</span>
            </h2>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link
              to="/request-access"
              data-testid="footer-cta"
              className="inline-flex items-center gap-2 rounded-md bg-white text-ink px-5 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
            >
              Request access <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-200 p-2">
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
      <div className="font-display text-base font-medium">{value}</div>
    </div>
  );
}

function FeatureTile({ icon: Icon, title, copy, className = "" }) {
  return (
    <div className={`rounded-lg border border-zinc-200 bg-white ${className}`}>
      <Icon className="h-5 w-5 text-ink" />
      <div className="mt-6 font-display text-xl font-medium">{title}</div>
      <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{copy}</p>
    </div>
  );
}

function YachtCard({ className = "", name, sub, img }) {
  return (
    <div className={`rounded-lg overflow-hidden border border-zinc-200 bg-white ${className}`}>
      <div className="aspect-[16/10] overflow-hidden">
        <img src={img} alt={name} className="h-full w-full object-cover" />
      </div>
      <div className="p-4 flex items-center justify-between">
        <div>
          <div className="font-display text-lg font-medium leading-tight">{name}</div>
          <div className="text-xs text-zinc-500">{sub}</div>
        </div>
        <div className="text-xs px-2 py-1 rounded-full border border-zinc-200">Active</div>
      </div>
    </div>
  );
}

function RoleCard({ title, copy, img, to }) {
  return (
    <Link to={to} className="group rounded-lg border border-zinc-200 bg-white overflow-hidden block">
      <div className="aspect-[4/3] overflow-hidden">
        <img src={img} alt={title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-5">
        <div className="font-display text-lg font-medium">{title}</div>
        <p className="mt-1 text-sm text-zinc-600">{copy}</p>
        <div className="mt-3 inline-flex items-center gap-1 text-xs text-ink">
          Read more <ArrowUpRight className="h-3 w-3" />
        </div>
      </div>
    </Link>
  );
}
